import Article from './article';
import Center from '../../components/center';

const title = 'Images in a responsive Web';

const ArticleContent = () => (
	<Article title={title}>
		<Center>
			<h1>{title}</h1>
			<p class="entry">
				Fluid content and any screen size are foundational notions of responsive
				Web design. However, figuring out how to best implement images in a
				responsive Web site is a problem with as many questions as answers.
			</p>

			<p>
				As the viewport changes size and different media queries are applied to
				adjust the layout of the content, images often change size from small to
				large and anywhere in between. Additionally, pixel dense displays, which
				look best when images of twice the width and height are served, are
				becoming increasingly more common. To account for this change in size
				and pixel density, some sites have begun to serve files with different
				dimensions to different users. But therein lays the problem: who should
				get which image?
			</p>

			<p>
				To solve for this void with standard HTML several people have come
				together to{' '}
				<a href="https://www.w3.org/community/respimg/wiki/Picture_Element_Proposal">
					propose the picture element
				</a>
				. For the <a href="https://www.microsoft.com/">Microsoft.com</a> homepage
				we came at it from a few different angles. These and others are listed
				below.
			</p>

			<p>
				For some graphics it is possible to use icon fonts or SVG to forego
				images and have graphics that scale beautifully. We chose to use an icon
				font for some icons on the page but I will save that for another post.
			</p>

			<h3>2x images</h3>

			<p>
				To account for high density displays, some people's first instinct is to
				just serve everyone a 2x version of the image. Unfortunately this
				requires most users to download a larger image than is needed. In some
				cases however, this might be an acceptable solution.
			</p>

			<h3>1x images</h3>

			<p>
				Other people take a much simpler approach and continue to serve everyone
				a 1x version of the image. While this optimizes for quick downloads, it
				can result in images looking pixelated and blurry on HD displays. In
				some cases however, you may find that it is not worth the extra bytes to
				download the higher quality image.
			</p>

			<h3>1._x images</h3>

			<p>
				You can always try meeting in the middle with 1.5x images. They are not
				as blurry as a 1x image on a HD display and won’t take quite as long to
				download as a 2x image. You can fine tune the balance of blur and file
				size by trying other sizes: 1.2x, 1.4x, 1.7x, etc.
			</p>

			<figure>
				<a native href="/images/sizes-1.jpg" class="img-link">
					<img
						class="alignnone size-full wp-image-146"
						title="sizes-1"
						src="/images/sizes-1.jpg"
						alt=""
					/>
				</a>
				<figcaption>
					See{' '}
					<a native href="/images/sizes-1.jpg">
						full-size
					</a>{' '}
					image for best comparison. Screenshot from iPad with Retina display.
				</figcaption>
			</figure>

			<h3>Picture element polyfill</h3>
			<p>
				When a single image size doesn’t do the trick, you can consider a whole
				load of other options with a picture polyfill. The polyfill example
				include in the{' '}
				<a href="https://www.w3.org/community/respimg/wiki/Picture_Element_Proposal">
					W3C proposal
				</a>{' '}
				is a branch of the{' '}
				<a href="https://github.com/scottjehl/picturefill">picturefill</a> work
				done by <a href="https://twitter.com/scottjehl">Scott Jehl</a>. The
				largest advantage I see to this becoming a standard element, besides not
				having to include another JavaScript file, is being able to have the
				browser request images based of preferences set by the user (to account
				for low bandwidth situations). For the Microsoft.com homepage we looked
				at the work Scott had done and the W3C proposal and rolled our own
				version to meet our requirements. We are making our branch publicly
				available to{' '}
				<a href="https://github.com/tysonmatanich/picturefill">download</a>.
			</p>

			<p>Some of the things we added:</p>
			<ul>
				<li>Allow specific images to load before the entire DOM is ready</li>
				<li>
					Allow specific images to only load after the rest of the page loads
				</li>
				<li>Disable swapping out of specific images when needing to shrink</li>
				<li>Disable swapping out of specific images when needing to grow</li>
				<li>
					Wait for the new image to complete downloading before updating the img
					element
				</li>
				<li>Load images based on the physical device pixel width</li>
				<li>
					Render inner HTML of noscript element if no sources are supplied
				</li>
			</ul>

			<p>
				The following is an example of the markup used to render the Microsoft
				logo in our header:
			</p>
			<pre>
				<code>
					{`<div data-picture data-alt="Microsoft">
    <div data-src="http://i.s-microsoft.com/.../logo-lg-1x.png"></div>
    <div data-src="http://i.s-microsoft.com/.../logo-lg-2x.png" data-media="(min-device-pixel-ratio: 2.0)"></div>
    <div data-src="http://i.s-microsoft.com/.../logo-sm-1x.png" data-media="(max-width: 539px)"></div>
    <div data-src="http://i.s-microsoft.com/.../logo-sm-2x.png" data-media="(max-width: 539px) and (min-device-pixel-ratio: 2.0)"></div>
    <noscript><img src="http://i.s-microsoft.com/.../logo-lg-1x.png" alt="Microsoft" /></noscript>
</div>`}
				</code>
			</pre>

			<p>
				The following is an example of the markup used to render a hero image:
			</p>
			<pre>
				<code>
					{`<div data-picture data-alt="Meet Windows 8." data-disable-swap-below>
    <div data-src="http://i.s-microsoft.com/.../1600x540.jpg"></div>
    <div data-src="http://i.s-microsoft.com/.../1024x346.jpg" data-media="(max-device-pixel-width:1024px)"></div>
    <div data-src="http://i.s-microsoft.com/.../600x203.jpg" data-media="(max-device-pixel-width:600px)"></div>
    <div data-src="http://i.s-microsoft.com/.../480x162.jpg" data-media="(max-device-pixel-width:480px)"></div>
    <noscript><img src="http://i.s-microsoft.com/.../1600x540.jpg" alt="Meet Windows 8." /></noscript>
</div>`}
				</code>
			</pre>

			<p>
				To learn more about our picture polyfill{' '}
				<a href="https://github.com/tysonmatanich/picturefill">
					view our branch
				</a>
				. We hope to have more documentation posted soon.
			</p>
			<h3>Image types and scaling</h3>
			<p>
				When analyzing images on the homepage we noticed a few different things.
				These may not be all inclusive and are only meant to represent our page
				at this point in time.
			</p>

			<p>Images contain different types of content:</p>
			<ul>
				<li>Logo</li>
				<li>Text</li>
				<li>Photograph</li>
				<li>Illustration</li>
				<li>Mixture of above</li>
			</ul>

			<p>
				We then looked to see how each of these image types looked on a HD
				display.
			</p>

			<figure>
				<a native href="/images/sizes-3.jpg" class="img-link">
					<img
						class="alignnone size-full wp-image-145"
						title="sizes-3"
						src="/images/sizes-3.jpg"
						alt=""
					/>
				</a>
				<figcaption>
					See{' '}
					<a native href="/images/sizes-3.jpg">
						full-size
					</a>{' '}
					image for best comparison. Screenshot from iPad with Retina display.
				</figcaption>
			</figure>

			<p>
				As you can see the 1x versions of all types appear to look considerably
				worse than their 1.5x or 2x counterparts when viewed on a HD display. We
				took this into account and then looked at how the images would rescale
				on the page.
			</p>

			<p>Images sizes change for different reasons:</p>
			<ul>
				<li>Fixed size, never changes</li>
				<li>Two different sizes depending on the viewport width</li>
				<li>
					Width is equal to the viewport width but then reaches a defined
					max-width (1600px)
				</li>
				<li>
					Width changes proportional to its container instead of the viewport
					width
				</li>
			</ul>

			<p>
				With this information we were able to start considering how to implement
				images on different areas of the page depending on what type of content
				they contain and how they rescale.
			</p>

			<p>What we came up with so far:</p>
			<ul>
				<li>
					Header logo – 4 sizes with picture polyfill based on viewport width
					and pixel density
				</li>
				<li>Icons – Web font with fallback to 1x images</li>
				<li>
					Hero image – 4 sizes with picture polyfill based on physical screen
					width
				</li>
				<li>Product logos in tile overlay – 2x images</li>
				<li>
					For home / For work images – Images scaling from 1.4x to 3.5x (at
					320px viewport) depending on viewport width
				</li>
				<li>News Center images – 1.2x images</li>
				<li>Footer logos – 2x images</li>
				<li>
					Footer Microsoft logo – 2 sizes with picture polyfill based on pixel
					density
				</li>
			</ul>

			<p>
				As you can see, we are doing a bit of everything depending on the
				specific case; which I think is a good thing. We will be revaluating our
				approach as more HD displays come to market so expect things to change a
				bit.
			</p>

			<h3>Which is better 1x, 2x, 1._x or picture element?</h3>
			<p>
				Let’s take the Microsoft.com footer image as an example. For these
				images we opted for 2x images (52 x 52 scaled to 26 x 26) to ensure
				quality on pixel dense displays while still keeping the file size small.
			</p>

			<figure>
				<a native href="/images/sizes-2.png" class="img-link">
					<img
						class="alignnone size-full wp-image-144"
						title="sizes-2"
						src="/images/sizes-2.png"
						alt=""
					/>
				</a>
				<figcaption>
					See{' '}
					<a native href="/images/sizes-2.png">
						full-size
					</a>{' '}
					image for best comparison. Screenshot from iPad with Retina display.
				</figcaption>
			</figure>

			<p>
				As you can see the difference between the 1x and 2x images is 648 bytes.
				Some people might think that the picture polyfill would work great here;
				however it takes approximately 500 bytes worth of markup (not including
				our JavaScript library) to render the mock picture element. This would
				require an even larger download for HD users and a download of almost
				the same size as the 2x for non-HD users.
			</p>

			<h3>Device width vs. viewport width</h3>
			<p>
				On most phones and tablets viewport width and device width are the same
				thing. However, on laptops and desktops viewport is equal to the inner
				width of the browser window and device width is equal to the display
				size. Device width can get messy when using multiple monitors as
				browsers implement this differently. IE will report the width of your
				main display while Chrome and Firefox will usually report the width of
				the current display (sometimes it seems to lose track of which display
				it is on).
			</p>

			<p>
				How to obtain the device pixel width with JavaScript (
				<a href="https://github.com/tysonmatanich/GetDevicePixelWidth">
					download source
				</a>
				):
			</p>

			<pre>
				<code>
					{`/*! GetDevicePixelWidth | Includes: GetDevicePixelRatio | Author: Tyson Matanich, 2012 | License: MIT */
(function (n) {
    n.getDevicePixelWidth = function (t) {
        t = t || !1;
        var i = t == !1 || n.screen.width > n.screen.height ? n.screen.width : n.screen.height,
            r = t == !1 || n.innerWidth > n.innerHeight ? n.innerWidth : n.innerHeight;
        return r > i && (i = r), i * n.getDevicePixelRatio()
    }
})(this);`}
				</code>
			</pre>

			<p>
				How to obtain the viewport width with JavaScript (
				<a href="https://github.com/tysonmatanich/GetViewportWidth">
					download source
				</a>
				):
			</p>

			<pre>
				<code>
					{`/*! GetViewportWidth | Author: Tyson Matanich, 2012 | License: MIT */
(function (n) {
    n.getViewportWidth = function () {
        var u, f = n.document,
            t = f.documentElement,
            i, r;
        return n.innerWidth === undefined ? u = t.clientWidth : n.innerWidth > t.clientWidth ? (i = f.createElement("body"), i.id = "vpw-test-b", i.style.cssText = "overflow:scroll", r = f.createElement("div"), r.id = "vpw-test-d", r.style.cssText = "position:absolute;top:-1000px", r.innerHTML = "<style>@media(width:" + t.clientWidth + "px){body#vpw-test-b div#vpw-test-d{width:7px!important}}</style>", i.appendChild(r), t.insertBefore(i, f.head), u = r.offsetWidth == 7 ? t.clientWidth : n.innerWidth, t.removeChild(i)) : u = n.innerWidth, u
    }
})(this);`}
				</code>
			</pre>
			<h3>Detecting a high-density display</h3>
			<p>
				In WebKit based browsers, the pixel density of the display can be
				detected with JavaScript using <code>window.devicePixelRatio</code>. An
				iPhone 4s will return a value of 2. However there can be a bit of{' '}
				<a href="https://www.quirksmode.org/blog/archives/2012/07/more_about_devi.html">
					inconsistency
				</a>{' '}
				in various browsers.
			</p>

			<p>
				In Internet Explorer 10 you will need to calculate the device pixel
				ratio, which at first may seem annoying but in the end provides much
				more flexibility.
			</p>

			<p>A calculation for device pixel ratio in Internet Explorer 10:</p>

			<pre>
				<code>window.screen.systemXDPI / window.screen.logicalXDPI</code>
			</pre>

			<p>
				A calculation for device pixel ratio accounting for zoom in Internet
				Explorer 10:
			</p>

			<pre>
				<code>window.screen.deviceXDPI / window.screen.logicalXDPI</code>
			</pre>

			<p>
				Obtaining device pixel ratio across browsers (
				<a href="https://github.com/tysonmatanich/GetDevicePixelRatio">
					download source
				</a>
				):
			</p>

			<pre>
				<code>
					{`/*! GetDevicePixelRatio | Author: Tyson Matanich, 2012 | License: MIT */
(function (n) {
    n.getDevicePixelRatio = function () {
    var t = 1;
    return n.screen.systemXDPI !== undefined && n.screen.logicalXDPI !== undefined && n.screen.systemXDPI > n.screen.logicalXDPI ? t = n.screen.systemXDPI / n.screen.logicalXDPI : n.devicePixelRatio !== undefined && (t = n.devicePixelRatio), t
    }
})(this);`}
				</code>
			</pre>

			<h3>A pixel is not always a pixel... what?!</h3>
			<p>
				Most of the{' '}
				<a href="https://www.w3.org/TR/css3-mediaqueries/#device-width">
					media queries
				</a>{' '}
				work great if you need to download images based on the viewport or
				device width; however both of these use{' '}
				<a href="https://www.w3.org/TR/css3-mediaqueries/#units">CSS pixels</a>{' '}
				of the logical kind not the actual physical pixels. When you need to use
				the actual physical pixels you will have to use a bit of JavaScript (see
				code examples above).
			</p>

			<h3>So what’s the answer?</h3>
			<p>
				As you can see there are a lot of different ways to approach the issue,
				and probably several more that I have not covered or thought of. It will
				be interesting to see what solutions are produced in the next few years
				as more HD devices are released and more Web sites start to serve HD
				images.
			</p>
		</Center>
	</Article>
);

export default ArticleContent;
